import { useCallback, useRef } from 'react';

export const ROVING_KEY = {
    ARROW_LEFT: 'ArrowLeft',
    ARROW_RIGHT: 'ArrowRight',
    ARROW_UP: 'ArrowUp',
    ARROW_DOWN: 'ArrowDown',
    HOME: 'Home',
    END: 'End',
    ENTER: 'Enter',
    SPACE: ' ',
};

export const ROVING_ORIENTATION = {
    HORIZONTAL: 'horizontal',
    VERTICAL: 'vertical',
    /** Both arrow pairs: Right/Down move forward, Left/Up back (ARIA radio group) */
    BOTH: 'both',
};

const MOVE = { NEXT: 'next', PREV: 'prev', FIRST: 'first', LAST: 'last' };

const ARROW_MOVES = {
    [ROVING_ORIENTATION.HORIZONTAL]: {
        [ROVING_KEY.ARROW_RIGHT]: MOVE.NEXT,
        [ROVING_KEY.ARROW_LEFT]: MOVE.PREV,
    },
    [ROVING_ORIENTATION.VERTICAL]: {
        [ROVING_KEY.ARROW_DOWN]: MOVE.NEXT,
        [ROVING_KEY.ARROW_UP]: MOVE.PREV,
    },
    [ROVING_ORIENTATION.BOTH]: {
        [ROVING_KEY.ARROW_RIGHT]: MOVE.NEXT,
        [ROVING_KEY.ARROW_DOWN]: MOVE.NEXT,
        [ROVING_KEY.ARROW_LEFT]: MOVE.PREV,
        [ROVING_KEY.ARROW_UP]: MOVE.PREV,
    },
};

const EDGE_MOVES = { [ROVING_KEY.HOME]: MOVE.FIRST, [ROVING_KEY.END]: MOVE.LAST };

const ACTIVATION_KEYS = new Set([ROVING_KEY.ENTER, ROVING_KEY.SPACE]);

const NEVER_DISABLED = () => false;

const firstEnabled = (indices, isDisabled) => indices.find((index) => !isDisabled(index)) ?? -1;

const range = (count) => Array.from({ length: Math.max(count, 0) }, (_, index) => index);

/**
 * Return the index a navigation key moves to: -1 when there is nowhere to go, null when the key
 * is not a navigation key for this orientation. Disabled items are skipped; without `wrap` the
 * walk stops at the ends.
 */
export const getRovingTargetIndex = ({
    key,
    index,
    count,
    orientation = ROVING_ORIENTATION.BOTH,
    wrap = true,
    isDisabled = NEVER_DISABLED,
}) => {
    const move = ARROW_MOVES[orientation]?.[key] ?? EDGE_MOVES[key];
    if (!move) return null;
    if (move === MOVE.FIRST) return firstEnabled(range(count), isDisabled);
    if (move === MOVE.LAST) return firstEnabled(range(count).reverse(), isDisabled);

    const delta = move === MOVE.NEXT ? 1 : -1;
    const walk = range(count - 1)
        .map((step) => index + delta * (step + 1))
        .map((candidate) => (wrap ? ((candidate % count) + count) % count : candidate))
        .filter((candidate) => candidate >= 0 && candidate < count);
    return firstEnabled(walk, isDisabled);
};

/** Return the one tabbable index: the active item when enabled, else the first enabled one. */
export const getTabbableIndex = ({ activeIndex, count, isDisabled = NEVER_DISABLED }) =>
    activeIndex >= 0 && activeIndex < count && !isDisabled(activeIndex)
        ? activeIndex
        : firstEnabled(range(count), isDisabled);

/**
 * Roving-tabindex keyboard handling for a group of items (radio group, tablist, stepper).
 * Arrows (per `orientation`) and Home/End move focus to the target item and call
 * `onSelect(index)`; a key that lands on the item it was pressed on does nothing. Enter/Space
 * call `onActivate(index)` when given.
 *
 * @example
 * const { getItemRef, handleKeyDown, tabbableIndex } = useRovingFocus({
 *     count: options.length,
 *     activeIndex: selectedIndex,
 *     onSelect: (index) => onChange(options[index].id),
 * });
 * options.map((option, index) => (
 *     <button
 *         ref={getItemRef(index)}
 *         tabIndex={index === tabbableIndex ? 0 : -1}
 *         onKeyDown={(event) => handleKeyDown(event, index)}
 *     />
 * ));
 */
const useRovingFocus = ({
    count,
    activeIndex = -1,
    orientation = ROVING_ORIENTATION.BOTH,
    wrap = true,
    isDisabled = NEVER_DISABLED,
    onSelect = null,
    onActivate = null,
}) => {
    const itemRefs = useRef([]);

    const getItemRef = useCallback(
        (index) => (node) => {
            itemRefs.current[index] = node;
        },
        [],
    );

    const focusItem = useCallback((index) => itemRefs.current[index]?.focus(), []);

    const handleKeyDown = (event, index) => {
        const { key } = event;
        if (onActivate && ACTIVATION_KEYS.has(key)) {
            event.preventDefault();
            onActivate(index);
            return;
        }
        const target = getRovingTargetIndex({ key, index, count, orientation, wrap, isDisabled });
        if (target === null) return;
        event.preventDefault();
        if (target === -1 || target === index) return;
        focusItem(target);
        onSelect?.(target);
    };

    return {
        getItemRef,
        handleKeyDown,
        focusItem,
        tabbableIndex: getTabbableIndex({ activeIndex, count, isDisabled }),
    };
};

export default useRovingFocus;
